import React, { useEffect } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useSearchStore } from '../../store/searchStore';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { getRecentSearches, pushRecentSearch } from '../../lib/recentSearches';
import { PersonRow, OrganizationRow, TaskRow } from './SearchResultRow';
import type {
  SearchPersonResult,
  SearchOrganizationResult,
  SearchTaskResult,
} from '../../types';

export function GlobalSearch() {
  const isOpen = useSearchStore((s) => s.isOpen);
  const close = useSearchStore((s) => s.close);
  const navigate = useNavigate();
  const { query, setQuery, results, counts, isLoading } = useGlobalSearch();

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen, setQuery]);

  if (!isOpen) return null;

  const recents = getRecentSearches();
  const hasQuery = query.trim().length >= 2;
  const hasAnyResult =
    (results.people?.length ?? 0) +
      (results.organizations?.length ?? 0) +
      (results.tasks?.length ?? 0) >
    0;

  const goToPerson = (p: SearchPersonResult) => {
    pushRecentSearch({ type: 'person', data: p });
    close();
    navigate(`/people/${p.id}`);
  };
  const goToOrg = (o: SearchOrganizationResult) => {
    pushRecentSearch({ type: 'organization', data: o });
    close();
    navigate(`/organizations/${o.id}`);
  };
  const goToTask = (t: SearchTaskResult) => {
    pushRecentSearch({ type: 'task', data: t });
    close();
    navigate(t.person ? `/people/${t.person.id}` : '/activities');
  };
  const goCreatePerson = () => {
    const parts = query.trim().split(/\s+/);
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ');
    close();
    navigate(`/people/new?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center md:pt-[10vh] bg-black/60"
      onClick={close}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-800 w-full md:max-w-2xl md:rounded-lg md:border md:border-gray-200 md:dark:border-gray-700 shadow-2xl flex flex-col h-full md:h-auto md:max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Global search"
      >
        <Command
          label="Global search"
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-gray-500 [&_[cmdk-group-heading]]:dark:text-gray-400"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <Search className="w-5 h-5 text-gray-400" aria-hidden="true" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search people, organizations, tasks…"
              type="search"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="flex-1 bg-transparent outline-none text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
            />
            <button
              type="button"
              onClick={close}
              aria-label="Close search"
              className="w-11 h-11 -mr-2 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isLoading && <div className="h-0.5 bg-blast-400 animate-pulse" />}

          <Command.List className="flex-1 overflow-y-auto py-2">
            {!hasQuery && recents.length > 0 && (
              <Command.Group heading="Recent">
                {recents.map((r) =>
                  r.type === 'person' ? (
                    <Command.Item key={`p-${r.data.id}`} value={`p-${r.data.id}`} onSelect={() => goToPerson(r.data)}>
                      <PersonRow person={r.data} />
                    </Command.Item>
                  ) : r.type === 'organization' ? (
                    <Command.Item key={`o-${r.data.id}`} value={`o-${r.data.id}`} onSelect={() => goToOrg(r.data)}>
                      <OrganizationRow org={r.data} />
                    </Command.Item>
                  ) : (
                    <Command.Item key={`t-${r.data.id}`} value={`t-${r.data.id}`} onSelect={() => goToTask(r.data)}>
                      <TaskRow task={r.data} />
                    </Command.Item>
                  ),
                )}
              </Command.Group>
            )}

            {!hasQuery && (
              <Command.Group heading="Jump to">
                {[
                  { label: 'Dashboard', path: '/' },
                  { label: 'People', path: '/people' },
                  { label: 'Organizations', path: '/organizations' },
                  { label: 'Activities', path: '/activities' },
                ].map((link) => (
                  <Command.Item
                    key={link.path}
                    value={`jump-${link.path}`}
                    onSelect={() => {
                      close();
                      navigate(link.path);
                    }}
                  >
                    <div className="flex items-center gap-3 w-full px-4 py-2.5 min-h-[40px] md:min-h-0 md:py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                      <span className="text-sm text-gray-900 dark:text-gray-100">{link.label}</span>
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {hasQuery && (results.people?.length ?? 0) > 0 && (
              <Section heading="People" total={counts.people} onSeeAll={() => { close(); navigate(`/people?search=${encodeURIComponent(query)}&scope=all`); }}>
                {results.people!.map((p) => (
                  <Command.Item key={`p-${p.id}`} value={`p-${p.id}`} onSelect={() => goToPerson(p)}>
                    <PersonRow person={p} />
                  </Command.Item>
                ))}
              </Section>
            )}

            {hasQuery && (results.organizations?.length ?? 0) > 0 && (
              <Section heading="Organizations" total={counts.organizations} onSeeAll={() => { close(); navigate(`/organizations?search=${encodeURIComponent(query)}&scope=all`); }}>
                {results.organizations!.map((o) => (
                  <Command.Item key={`o-${o.id}`} value={`o-${o.id}`} onSelect={() => goToOrg(o)}>
                    <OrganizationRow org={o} />
                  </Command.Item>
                ))}
              </Section>
            )}

            {hasQuery && (results.tasks?.length ?? 0) > 0 && (
              <Section heading="Tasks" total={counts.tasks} onSeeAll={() => { close(); navigate('/activities'); }}>
                {results.tasks!.map((t) => (
                  <Command.Item key={`t-${t.id}`} value={`t-${t.id}`} onSelect={() => goToTask(t)}>
                    <TaskRow task={t} />
                  </Command.Item>
                ))}
              </Section>
            )}

            {hasQuery && !isLoading && !hasAnyResult && (
              <Command.Group heading="No results">
                <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                  Nothing matched “{query}”.
                </div>
                <Command.Item value="create-new-person" onSelect={goCreatePerson}>
                  <div className="flex items-center gap-3 w-full px-4 py-2.5 min-h-[40px] md:min-h-0 md:py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                    <span className="text-sm text-blast-700 dark:text-blast-400 font-medium">
                      + Create new person: “{query}”
                    </span>
                  </div>
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}

function Section({
  heading,
  total,
  onSeeAll,
  children,
}: {
  heading: string;
  total: number | undefined;
  onSeeAll: () => void;
  children: React.ReactNode;
}) {
  const visible = React.Children.count(children);
  const showSeeAll = typeof total === 'number' && total > visible;
  return (
    <Command.Group heading={heading}>
      {children}
      {showSeeAll && (
        <Command.Item value={`seeall-${heading}`} onSelect={onSeeAll}>
          <div className="flex items-center w-full px-4 py-2 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
            See all {total} results in {heading} →
          </div>
        </Command.Item>
      )}
    </Command.Group>
  );
}
